<template>
  <div class="courses-admin-shell py-8 px-4">
    <v-container class="admin-container">
      <!-- Back to Dashboard -->
      <div class="d-flex align-center mb-4">
        <v-btn text color="grey lighten-1" class="rounded-lg pl-0 text-capitalize font-weight-bold" @click="$router.push('/dashboard')">
          <v-icon left>mdi-arrow-left</v-icon> Zurück zum Dashboard
        </v-btn>
      </div>

      <!-- Tabs Navigation -->
      <v-tabs v-model="activeTab" grow dark background-color="transparent" color="#fbc02d" class="mb-6">
        <v-tab href="#timeslots" class="text-capitalize font-weight-bold"> <v-icon left>mdi-calendar-clock</v-icon> Kursplanungen </v-tab>
        <v-tab href="#formats" class="text-capitalize font-weight-bold"> <v-icon left>mdi-format-list-bulleted</v-icon> Kursformate </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" style="background-color: transparent">
        <!-- TAB 1: TIMESLOTS (COURSES EXECUTION) -->
        <v-tab-item value="timeslots">
          <!-- Header -->
          <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-6">
            <div>
              <h1 class="text-h4 font-weight-black white--text italic-title">WELL COME FIT</h1>
              <h2 class="subtitle-1 yellow--text text--darken-2 font-weight-bold text-uppercase tracking-wider">Kurs & Lektionen Manager</h2>
            </div>
            <div class="d-flex flex-wrap gap-3 mt-4 mt-sm-0">
              <v-btn outlined color="#fbc02d" class="font-weight-bold rounded-lg" @click="$router.push('/employee-courses')">
                <v-icon left>mdi-eye-outline</v-icon> Vorschau Kursplan
              </v-btn>
              <v-btn color="#fbc02d" class="black--text font-weight-bold rounded-lg" @click="openCreateDialog" :disabled="activeFormats.length === 0">
                <v-icon left>mdi-plus</v-icon> Kurs erfassen
              </v-btn>
              <v-btn text outlined color="grey lighten-1" @click="fetchData"> <v-icon left>mdi-refresh</v-icon> Aktualisieren </v-btn>
            </div>
          </div>

          <!-- Alert if no formats exist -->
          <v-alert v-if="activeFormats.length === 0" type="warning" outlined dense class="mb-6">
            Bevor du einen Kurs planen kannst, musst du im Reiter "Kursformate" mindestens ein aktives Kursformat erfassen!
          </v-alert>

          <!-- Stats Cards -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" md="4">
              <v-card dark class="stat-card pa-4 rounded-xl">
                <div class="caption grey--text">Geplante Kurse</div>
                <div class="text-h4 font-weight-black white--text my-1">
                  {{ activeCoursesCount }}
                </div>
                <div class="caption grey--text mt-2">Aktive Timeslots im System</div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <v-card dark class="stat-card pa-4 rounded-xl">
                <div class="caption grey--text">Gesamte Buchungen</div>
                <div class="text-h4 font-weight-black white--text my-1">
                  {{ totalRegistrationsCount }}
                </div>
                <div class="caption grey--text mt-2">Angemeldete Mitarbeiter</div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <v-card dark class="stat-card pa-4 rounded-xl">
                <div class="caption grey--text">Wartelisten Belegung</div>
                <div class="text-h4 font-weight-black orange--text text--lighten-2 my-1">
                  {{ totalWaitlistCount }}
                </div>
                <div class="caption grey--text mt-2">Mitarbeiter auf Wartelisten</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- DESKTOP VIEW: Grouped daily tables -->
          <div class="d-none d-sm-block">
            <v-card dark class="table-card rounded-xl pa-4">
              <!-- Header Controls -->
              <div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between mb-4 gap-3">
                <div class="d-flex align-center gap-4">
                  <h3 class="text-h6 font-weight-bold white--text mr-4">Kursplanungen</h3>
                  <v-tabs
                    v-model="timeslotsSubTab"
                    dense
                    dark
                    background-color="transparent"
                    color="#fbc02d"
                    slider-color="#fbc02d"
                    style="max-width: 300px">
                    <v-tab href="#upcoming" class="text-capitalize font-weight-bold">Anstehend</v-tab>
                    <v-tab href="#past" class="text-capitalize font-weight-bold">Vergangen</v-tab>
                  </v-tabs>
                </div>
              </div>

              <!-- List of Grouped Daily Tables -->
              <div v-if="filteredGroupedCourses.length === 0" class="text-center py-12 empty-state rounded-2xl">
                <v-icon size="48" color="grey darken-2" class="mb-2">mdi-calendar-remove</v-icon>
                <div class="grey--text subtitle-1">Keine Kurse für diese Auswahl gefunden.</div>
              </div>
              <div v-else>
                <div v-for="group in filteredGroupedCourses" :key="group.date" class="mb-6">
                  <!-- Date Header -->
                  <div class="d-flex align-center mb-3">
                    <span class="date-header-dot mr-2"></span>
                    <span class="body-1 font-weight-black white--text">{{ group.date }}</span>
                  </div>

                  <v-data-table
                    :headers="desktopHeaders"
                    :items="group.courses"
                    hide-default-footer
                    disable-pagination
                    class="custom-table border-thin-outline"
                    dark>
                    <!-- Time Format -->
                    <template v-slot:item.dateTime="{ item }">
                      <div>
                        <span class="body-2 white--text font-weight-bold">{{ formatTime(item.dateTime) }} Uhr</span>
                        <span class="caption grey--text d-block">{{ item.duration }} Min. Dauer</span>
                      </div>
                    </template>

                    <!-- Format Title & Details -->
                    <template v-slot:item.title="{ item }">
                      <div>
                        <span class="body-2 font-weight-bold white--text">{{ item.format ? item.format.title : "Unbekannt" }}</span>
                        <span v-if="item.details" class="caption yellow--text text--darken-2 d-block font-italic">{{ item.details }}</span>
                      </div>
                    </template>

                    <!-- Studio -->
                    <template v-slot:item.studio="{ item }">
                      <span class="body-2">{{ item.studio ? item.studio.name : "WELL COME FIT" }}</span>
                    </template>

                    <!-- Registered Spots -->
                    <template v-slot:item.belegung="{ item }">
                      <div>
                        <span
                          class="body-2 font-weight-bold"
                          :class="item.registeredCount >= item.capacity ? 'yellow--text text--darken-2' : 'white--text'">
                          {{ item.registeredCount }} / {{ item.capacity }}
                        </span>
                        <v-chip x-small v-if="item.registeredCount >= item.capacity" color="orange" class="black--text font-weight-black ml-1"
                          >VOLL</v-chip
                        >
                      </div>
                    </template>

                    <!-- Waitlist -->
                    <template v-slot:item.waitlist="{ item }">
                      <div>
                        <span v-if="item.waitlistEnabled" class="body-2"> {{ item.waitlistCount }} wartend </span>
                        <v-chip x-small v-else outlined color="grey">Inaktiv</v-chip>
                      </div>
                    </template>

                    <!-- Status -->
                    <template v-slot:item.status="{ item }">
                      <v-chip x-small :color="item.status === 'active' ? 'green' : 'red'" outlined class="font-weight-bold">
                        {{ item.status === "active" ? "Aktiv" : "Abgesagt" }}
                      </v-chip>
                    </template>

                    <!-- Actions -->
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex justify-end gap-1">
                        <v-btn icon color="blue" small @click="openManageRegistrations(item)">
                          <v-icon small>mdi-account-multiple</v-icon>
                        </v-btn>
                        <v-btn icon color="green" small @click="openEditDialog(item)" :disabled="item.status === 'cancelled' || isPastCourse(item)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          color="red"
                          small
                          @click="confirmCancelCourse(item)"
                          :disabled="item.status === 'cancelled' || isPastCourse(item)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </div>
            </v-card>
          </div>

          <!-- MOBILE VIEW (d-block d-sm-none) -->
          <div class="d-block d-sm-none">
            <!-- Mobile Filters and Controls -->
            <div class="mb-4 d-flex flex-column gap-3">
              <v-tabs v-model="timeslotsSubTab" dense dark background-color="transparent" color="#fbc02d" slider-color="#fbc02d" class="width-100">
                <v-tab href="#upcoming" class="text-capitalize font-weight-bold">Anstehend</v-tab>
                <v-tab href="#past" class="text-capitalize font-weight-bold">Vergangen</v-tab>
              </v-tabs>

              <div class="d-flex justify-start align-center">
                <v-btn-toggle v-model="mobileViewMode" mandatory dense class="rounded-lg">
                  <v-btn value="calendar" small class="px-3 font-weight-bold text-capitalize">
                    <v-icon left small>mdi-calendar</v-icon> Kalender
                  </v-btn>
                  <v-btn value="list" small class="px-3 font-weight-bold text-capitalize">
                    <v-icon left small>mdi-format-list-bulleted</v-icon> Liste
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>

            <!-- Calendar Mode -->
            <div v-if="mobileViewMode === 'calendar'">
              <v-date-picker
                v-model="mobileCalendarDate"
                full-width
                dark
                color="#fbc02d"
                header-color="#1c1c1e"
                class="rounded-xl mb-4"
                locale="de-CH"
                first-day-of-week="1"
                :events="getCalendarEvents"
                event-color="amber"></v-date-picker>

              <div class="subtitle-2 white--text mb-3 font-weight-black d-flex align-center">
                <v-icon small color="#fbc02d" class="mr-1">mdi-calendar-check</v-icon>
                Planungen am {{ formatLocalDateString(mobileCalendarDate) }}
              </div>

              <!-- Timeslot list on selected day -->
              <div v-if="mobileSelectedDayCourses.length === 0" class="text-center py-8 empty-state-mobile rounded-xl">
                <v-icon color="grey darken-2" size="48">mdi-calendar-blank</v-icon>
                <div class="caption grey--text mt-2">Keine Lektionen für diesen Tag geplant.</div>
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div
                  v-for="course in mobileSelectedDayCourses"
                  :key="course._id"
                  class="mobile-course-card pa-4 rounded-xl relative-box"
                  :class="{ 'is-cancelled': course.status === 'cancelled', 'is-past': isPastCourse(course) }">
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div>
                      <span class="caption font-weight-black text-uppercase yellow--text text--darken-2">
                        {{ course.studio ? course.studio.name : "WELL COME FIT" }}
                      </span>
                      <h4 class="text-subtitle-1 font-weight-black white--text leading-tight mt-0-5">
                        {{ course.format ? course.format.title : "Kurs" }}
                      </h4>
                      <span v-if="course.details" class="caption font-italic grey--text d-block">{{ course.details }}</span>
                    </div>
                    <v-chip x-small :color="course.status === 'active' ? 'green' : 'red'" outlined class="font-weight-bold">
                      {{ course.status === "active" ? "Aktiv" : "Abgesagt" }}
                    </v-chip>
                  </div>

                  <div class="divider-thin my-2"></div>

                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon x-small color="#fbc02d" class="mr-1">mdi-clock-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ formatTime(course.dateTime) }} Uhr ({{ course.duration }}m) </span>
                    </div>

                    <div class="d-flex align-center">
                      <v-icon x-small color="#fbc02d" class="mr-1">mdi-account-group-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ course.registeredCount }} / {{ course.capacity }} Plätze </span>
                    </div>
                  </div>

                  <div class="d-flex justify-space-between align-center pt-1">
                    <span class="caption grey--text"> Warteliste: {{ course.waitlistEnabled ? `${course.waitlistCount} wartend` : "Inaktiv" }} </span>

                    <div class="d-flex gap-2">
                      <v-btn icon color="blue" small outlined class="rounded-lg" @click="openManageRegistrations(course)">
                        <v-icon small>mdi-account-multiple</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="green"
                        small
                        outlined
                        class="rounded-lg"
                        @click="openEditDialog(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="red"
                        small
                        outlined
                        class="rounded-lg"
                        @click="confirmCancelCourse(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- List Mode -->
            <div v-else>
              <div v-if="visibleCoursesFiltered.length === 0" class="text-center py-12 empty-state-mobile rounded-xl">
                <v-icon color="grey darken-2" size="48">mdi-calendar-remove</v-icon>
                <div class="caption grey--text mt-2">Keine Kursplanungen gefunden.</div>
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div
                  v-for="course in visibleCoursesFiltered"
                  :key="course._id"
                  class="mobile-course-card pa-4 rounded-xl relative-box"
                  :class="{ 'is-cancelled': course.status === 'cancelled', 'is-past': isPastCourse(course) }">
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div>
                      <span class="caption font-weight-black text-uppercase yellow--text text--darken-2">
                        {{ course.studio ? course.studio.name : "WELL COME FIT" }}
                      </span>
                      <h4 class="text-subtitle-1 font-weight-black white--text leading-tight mt-0-5">
                        {{ course.format ? course.format.title : "Kurs" }}
                      </h4>
                      <span v-if="course.details" class="caption font-italic grey--text d-block">{{ course.details }}</span>
                    </div>
                    <v-chip x-small :color="course.status === 'active' ? 'green' : 'red'" outlined class="font-weight-bold">
                      {{ course.status === "active" ? "Aktiv" : "Abgesagt" }}
                    </v-chip>
                  </div>

                  <div class="divider-thin my-2"></div>

                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon x-small color="#fbc02d" class="mr-1">mdi-calendar</v-icon>
                      <span class="caption font-weight-bold white--text">
                        {{ formatDateShortCompact(course.dateTime) }}
                      </span>
                    </div>

                    <div class="d-flex align-center">
                      <v-icon x-small color="#fbc02d" class="mr-1">mdi-clock-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ formatTime(course.dateTime) }} Uhr ({{ course.duration }}m) </span>
                    </div>

                    <div class="d-flex align-center">
                      <v-icon x-small color="#fbc02d" class="mr-1">mdi-account-group-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ course.registeredCount }} / {{ course.capacity }} </span>
                    </div>
                  </div>

                  <div class="d-flex justify-space-between align-center pt-1">
                    <span class="caption grey--text"> Warteliste: {{ course.waitlistEnabled ? `${course.waitlistCount} wartend` : "Inaktiv" }} </span>

                    <div class="d-flex gap-2">
                      <v-btn icon color="blue" small outlined class="rounded-lg" @click="openManageRegistrations(course)">
                        <v-icon small>mdi-account-multiple</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="green"
                        small
                        outlined
                        class="rounded-lg"
                        @click="openEditDialog(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="red"
                        small
                        outlined
                        class="rounded-lg"
                        @click="confirmCancelCourse(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-tab-item>

        <!-- TAB 2: COURSE FORMATS (VORLAGEN) -->
        <v-tab-item value="formats">
          <!-- Header -->
          <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-6">
            <div>
              <h1 class="text-h4 font-weight-black white--text italic-title">KURSFORMATE</h1>
              <h2 class="subtitle-1 yellow--text text--darken-2 font-weight-bold text-uppercase tracking-wider">Basisvorlagen verwalten</h2>
            </div>
            <div class="d-flex gap-3 mt-4 mt-sm-0">
              <v-btn color="#fbc02d" class="black--text font-weight-bold rounded-lg" @click="openCreateFormatDialog">
                <v-icon left>mdi-plus</v-icon> Format erstellen
              </v-btn>
            </div>
          </div>

          <!-- Formats Card / Table -->
          <v-card dark class="table-card rounded-xl pa-4">
            <v-data-table :headers="formatHeaders" :items="formats" class="custom-table" :items-per-page="10" dark>
              <!-- Status -->
              <template v-slot:item.active="{ item }">
                <v-chip x-small :color="item.active ? 'green' : 'grey'" outlined class="font-weight-bold">
                  {{ item.active ? "Aktiv" : "Ausgeblendet" }}
                </v-chip>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <div class="d-flex justify-end">
                  <v-btn icon color="green" small @click="openEditFormatDialog(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <!-- Create/Edit Course Dialog -->
    <v-dialog v-model="courseDialog.show" max-width="600px" persistent dark>
      <v-card class="pa-6 rounded-2xl border-highlight" style="background-color: #1c1c1e">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#fbc02d" class="mr-2">{{ courseDialog.isEdit ? "mdi-pencil" : "mdi-plus-box" }}</v-icon>
            {{ courseDialog.isEdit ? "Kurs bearbeiten" : "Kurs erfassen" }}
          </h2>
          <v-btn icon color="grey" @click="courseDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="courseForm" v-model="courseDialog.valid">
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                dark
                v-model="courseFormModel.format"
                :items="activeFormats"
                item-text="title"
                item-value="_id"
                label="Kursformat auswählen*"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Kursformat ist erforderlich']"
                class="rounded-lg search-field"></v-autocomplete>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="courseFormModel.details"
                label="Spezielle Details für diese Ausführung (z.B. Trainer, optional)"
                outlined
                dense
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                dark
                v-model="courseFormModel.studio"
                :items="studioSelectOptions"
                label="Studio (Standort)*"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Studio ist erforderlich']"
                class="rounded-lg search-field"></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.duration"
                label="Dauer (in Minuten)*"
                type="number"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Dauer ist erforderlich', (v) => v > 0 || 'Dauer muss grösser als 0 sein']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.dateTime"
                label="Datum & Startzeit*"
                type="datetime-local"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Datum und Uhrzeit erforderlich']"
                class="rounded-lg search-field"
                @input="onDateTimeInput"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.cancellationDeadline"
                label="Abmeldefrist (Standard: 1h vorher)*"
                type="datetime-local"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Abmeldefrist ist erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.capacity"
                label="Kapazität (Standard 20)*"
                type="number"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Kapazität erforderlich', (v) => v > 0 || 'Kapazität muss grösser als 0 sein']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-center pl-4">
              <v-checkbox
                v-model="courseFormModel.waitlistEnabled"
                label="Warteliste aktivieren"
                color="#fbc02d"
                hide-details
                class="mt-0"></v-checkbox>
            </v-col>
          </v-row>

          <v-alert type="info" outlined dense color="#fbc02d" class="caption mt-3 dark-alert" v-if="courseDialog.isEdit">
            Wichtig: Falls du die Kapazität erhöhst, werden Personen auf der Warteliste automatisch vom System nachnominiert.
          </v-alert>
        </v-form>

        <div class="d-flex justify-end gap-3 mt-4">
          <v-btn text color="grey" @click="courseDialog.show = false">Abbrechen</v-btn>
          <v-btn
            color="#fbc02d"
            class="black--text font-weight-bold rounded-lg"
            :disabled="!courseDialog.valid"
            :loading="courseDialog.saving"
            @click="submitCourseForm">
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Format Dialog -->
    <v-dialog v-model="formatDialog.show" max-width="500px" persistent dark>
      <v-card class="pa-6 rounded-2xl border-highlight" style="background-color: #1c1c1e">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#fbc02d" class="mr-2">{{ formatDialog.isEdit ? "mdi-pencil" : "mdi-plus-box" }}</v-icon>
            {{ formatDialog.isEdit ? "Kursformat bearbeiten" : "Kursformat erstellen" }}
          </h2>
          <v-btn icon color="grey" @click="formatDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="formatForm" v-model="formatDialog.valid">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="formatFormModel.title"
                label="Format-Name*"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Name ist erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formatFormModel.description"
                label="Standard-Beschreibung"
                outlined
                dense
                rows="4"
                class="rounded-lg search-field"></v-textarea>
            </v-col>

            <v-col cols="12" class="d-flex align-center pl-2" v-if="formatDialog.isEdit">
              <v-checkbox
                v-model="formatFormModel.active"
                label="Format aktiv (anzeigen in Auswahlliste)"
                color="#fbc02d"
                hide-details
                class="mt-0"></v-checkbox>
            </v-col>
          </v-row>

          <v-alert type="info" outlined dense color="#fbc02d" class="caption mt-3 dark-alert">
            Hinweis: Kursformate können nicht gelöscht werden, um bestehende Timeslots nicht zu beschädigen. Du kannst sie jedoch ausblenden
            (deaktivieren).
          </v-alert>
        </v-form>

        <div class="d-flex justify-end gap-3 mt-4">
          <v-btn text color="grey" @click="formatDialog.show = false">Abbrechen</v-btn>
          <v-btn
            color="#fbc02d"
            class="black--text font-weight-bold rounded-lg"
            :disabled="!formatDialog.valid"
            :loading="formatDialog.saving"
            @click="submitFormatForm">
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Manage Registrations Dialog -->
    <v-dialog v-model="regManager.show" max-width="850px" persistent dark>
      <v-card class="pa-6 rounded-2xl border-highlight" style="background-color: #1c1c1e">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <span class="caption yellow--text text--darken-2 font-weight-black text-uppercase">Anmeldungsverwaltung</span>
            <h2 class="text-h5 font-weight-black white--text italic-title" v-if="regManager.course">
              {{ regManager.course.format ? regManager.course.format.title : "Kurs" }}
            </h2>
            <span v-if="regManager.course && regManager.course.details" class="caption font-italic grey--text">{{ regManager.course.details }}</span>
          </div>
          <v-btn icon color="grey" @click="regManager.show = false" :disabled="regManager.loading">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-row class="mb-4">
          <!-- Manual Registration Search Subcard -->
          <v-col cols="12" md="6" class="pa-2">
            <v-card class="pa-4 rounded-xl border-accent-box" dark style="background-color: rgba(255, 255, 255, 0.02); height: 100%">
              <h4 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-2">Mitarbeiter manuell hinzufügen</h4>
              <!-- User Search Component -->
              <user-search
                class="pa-0 search-field rounded-lg mb-3"
                rights="gf_fetchEmployeeCourses"
                label="Suche (Vorname, Nachname, E-Mail)..."
                @update="onUserSelected"></user-search>

              <v-slide-y-transition>
                <div
                  v-if="regManager.selectedUser"
                  class="pa-3 rounded-lg"
                  style="background-color: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
                  <div class="body-2 font-weight-bold white--text">{{ regManager.selectedUser.vorname }} {{ regManager.selectedUser.nachname }}</div>
                  <div class="caption grey--text">{{ regManager.selectedUser.email }}</div>

                  <div class="d-flex justify-end mt-3">
                    <v-btn
                      color="#fbc02d"
                      small
                      class="black--text font-weight-bold rounded-lg"
                      :loading="regManager.addingUser"
                      @click="submitManualRegistration">
                      Buchen (Admin-Eintrag)
                    </v-btn>
                  </div>
                </div>
              </v-slide-y-transition>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="pa-2">
            <div class="pa-4 rounded-xl meta-box text-left" style="height: 100%">
              <h4 class="subtitle-2 font-weight-black white--text text-uppercase mb-2">Kursstatus</h4>
              <div class="d-flex justify-space-between mb-1">
                <span class="caption grey--text">Teilnehmer Plätze:</span>
                <span class="body-2 font-weight-bold white--text" v-if="regManager.course">
                  {{ regManager.registrations.filter((r) => r.status === "registered").length }} / {{ regManager.course.capacity }}
                </span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="caption grey--text">Auf Warteliste:</span>
                <span class="body-2 font-weight-bold orange--text text--lighten-2" v-if="regManager.course">
                  {{ regManager.registrations.filter((r) => r.status === "waitlisted").length }} Personen
                </span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Abmeldefrist:</span>
                <span class="body-2 font-weight-bold red--text text--lighten-1" v-if="regManager.course">
                  {{ formatShortDateTime(regManager.course.cancellationDeadline) }}
                </span>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Registrations Table -->
        <h4 class="subtitle-2 font-weight-black white--text text-uppercase mb-2">Teilnehmerliste</h4>
        <v-data-table
          :headers="regHeaders"
          :items="regManager.registrations"
          :loading="regManager.loading"
          class="custom-table"
          dense
          dark
          :items-per-page="10">
          <!-- User Details -->
          <template v-slot:item.user="{ item }">
            <div class="py-1">
              <span class="body-2 font-weight-bold white--text">
                {{ item.user ? `${item.user.vorname} ${item.user.nachname}` : "Gelöschter User" }}
              </span>
              <span class="caption grey--text d-block">{{ item.user ? item.user.email : "" }}</span>
            </div>
          </template>

          <!-- Status badge -->
          <template v-slot:item.status="{ item }">
            <v-chip x-small :color="getRegStatusColor(item)" class="font-weight-bold black--text">
              {{ getRegStatusText(item) }}
            </v-chip>
          </template>

          <!-- Registered At Date -->
          <template v-slot:item.registeredAt="{ item }">
            <span class="caption">{{ formatDateShort(item.registeredAt) }}</span>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn v-if="item.status !== 'cancelled'" text x-small color="red" class="font-weight-bold" @click="cancelUserRegistration(item)">
              Stornieren
            </v-btn>
            <span v-else class="caption grey--text italic">Storniert</span>
          </template>
        </v-data-table>

        <div class="d-flex justify-end mt-4">
          <v-btn text color="grey" @click="regManager.show = false" :disabled="regManager.loading">Schliessen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Cancel Course Dialog -->
    <v-dialog v-model="cancelCourseDialog.show" max-width="400px" dark>
      <v-card class="pa-4 rounded-xl" style="background-color: #1c1c1e">
        <h3 class="text-h6 font-weight-bold white--text mb-2">Kurs absagen?</h3>
        <p class="grey--text caption leading-relaxed mb-4">
          Möchtest du den Kurs <strong>{{ cancelCourseDialog.courseTitle }}</strong> wirklich absagen? Alle angemeldeten Mitarbeiter werden
          automatisch storniert und erhalten eine E-Mail-Absage vom System.
        </p>
        <div class="d-flex justify-end gap-3">
          <v-btn text color="grey" @click="cancelCourseDialog.show = false">Abbrechen</v-btn>
          <v-btn color="red" class="black--text font-weight-bold rounded-lg" :loading="cancelCourseDialog.loading" @click="executeCancelCourse">
            Ja, Kurs absagen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"

export default {
  name: "EmployeeCoursesAdmin",
  components: {
    userSearch,
  },
  data() {
    return {
      activeTab: "timeslots",
      timeslotsSubTab: "upcoming",
      mobileViewMode: "calendar",
      mobileCalendarDate: new Date().toISOString().split("T")[0],
      loading: true,
      search: "",
      courses: [],
      formats: [],
      studios: [],

      desktopHeaders: [
        { text: "Zeit", value: "dateTime" },
        { text: "Kursformat & Details", value: "title" },
        { text: "Standort (Studio)", value: "studio" },
        { text: "Belegung", value: "belegung", align: "center" },
        { text: "Warteliste", value: "waitlist", align: "center" },
        { text: "Status", value: "status", align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],

      formatHeaders: [
        { text: "Formatname", value: "title" },
        { text: "Beschreibung", value: "description", sortable: false },
        { text: "Status", value: "active", align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],

      courseDialog: {
        show: false,
        isEdit: false,
        valid: false,
        saving: false,
        editId: null,
      },

      courseFormModel: {
        format: "",
        details: "",
        studio: "",
        dateTime: "",
        duration: 60,
        capacity: 20,
        waitlistEnabled: true,
        cancellationDeadline: "",
      },

      formatDialog: {
        show: false,
        isEdit: false,
        valid: false,
        saving: false,
        editId: null,
      },

      formatFormModel: {
        title: "",
        description: "",
        active: true,
      },

      regManager: {
        show: false,
        course: null,
        loading: false,
        registrations: [],
        selectedUserId: null,
        selectedUser: null,
        addingUser: false,
      },

      regHeaders: [
        { text: "Mitarbeiter", value: "user" },
        { text: "Status", value: "status" },
        { text: "Anmeldedatum", value: "registeredAt" },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],

      cancelCourseDialog: {
        show: false,
        courseId: null,
        courseTitle: null,
        loading: false,
      },
    }
  },
  computed: {
    visibleCourses() {
      const now = new Date()
      if (this.timeslotsSubTab === "upcoming") {
        return this.courses.filter((c) => new Date(c.dateTime) >= now).sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
      } else {
        return this.courses.filter((c) => new Date(c.dateTime) < now).sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
      }
    },

    // Mobile list view courses (fully filtered by search)
    visibleCoursesFiltered() {
      let list = this.visibleCourses
      if (this.search) {
        const q = this.search.toLowerCase()
        list = list.filter((c) => {
          const titleMatch = c.format && c.format.title && c.format.title.toLowerCase().includes(q)
          const detailsMatch = c.details && c.details.toLowerCase().includes(q)
          const studioMatch = c.studio && c.studio.name && c.studio.name.toLowerCase().includes(q)
          return titleMatch || detailsMatch || studioMatch
        })
      }
      return list
    },

    // Desktop view grouped by day (retains search filters)
    filteredGroupedCourses() {
      let list = this.visibleCoursesFiltered

      const groups = []
      const indexMap = {}

      list.forEach((course) => {
        if (!course.dateTime) return
        const dateObj = new Date(course.dateTime)
        const dateKey = dateObj.toLocaleDateString("de-CH", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })

        if (indexMap[dateKey] === undefined) {
          indexMap[dateKey] = groups.length
          groups.push({
            date: dateKey,
            courses: [],
          })
        }

        groups[indexMap[dateKey]].courses.push(course)
      })

      return groups
    },

    // Mobile calendar day selected courses list
    mobileSelectedDayCourses() {
      let list = this.visibleCourses.filter((c) => {
        const courseDate = new Date(c.dateTime).toISOString().split("T")[0]
        return courseDate === this.mobileCalendarDate
      })

      if (this.search) {
        const q = this.search.toLowerCase()
        list = list.filter((c) => {
          const titleMatch = c.format && c.format.title && c.format.title.toLowerCase().includes(q)
          const detailsMatch = c.details && c.details.toLowerCase().includes(q)
          const studioMatch = c.studio && c.studio.name && c.studio.name.toLowerCase().includes(q)
          return titleMatch || detailsMatch || studioMatch
        })
      }

      return list
    },

    activeCoursesCount() {
      return this.courses.filter((c) => c.status === "active").length
    },
    totalRegistrationsCount() {
      return this.courses.reduce((sum, c) => sum + (c.registeredCount || 0), 0)
    },
    totalWaitlistCount() {
      return this.courses.reduce((sum, c) => sum + (c.waitlistCount || 0), 0)
    },
    cancellationRate() {
      return 0
    },
    studioSelectOptions() {
      return this.studios.map((s) => ({ text: s.name, value: s._id }))
    },
    activeFormats() {
      return this.formats.filter((f) => f.active === true)
    },
  },
  async created() {
    await this.loadStudios()
    await this.fetchData()
  },
  methods: {
    isPastCourse(item) {
      if (!item || !item.dateTime) return false
      return new Date(item.dateTime) < new Date()
    },

    async loadStudios() {
      try {
        let studios = await api.fetchStudios()
        this.studios = studios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
      } catch (e) {
        console.error("Failed to load studios:", e)
        this.studios = [
          { _id: "5f3c30bfd91ddb464c2648b2", name: "Frauenfeld Zentral" },
          { _id: "5f3c30bfd91ddb464c2648b3", name: "St. Gallen West" },
        ]
      }
    },

    async fetchData() {
      this.loading = true
      try {
        const [coursesData, formatsData] = await Promise.all([api.employeeCourses.admin.fetchCourses(), api.employeeCourses.admin.fetchFormats()])
        this.courses = coursesData || []
        this.formats = formatsData || []
      } catch (error) {
        console.error(error)
        this.$toast.error("Daten konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },

    formatTime(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    },

    formatDateShort(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    },

    formatDateShortCompact(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
      })
    },

    formatLocalDateString(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    },

    formatShortDateTime(dateStr) {
      if (!dateStr) return ""
      return (
        new Date(dateStr).toLocaleDateString("de-CH", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }) + " Uhr"
      )
    },

    formatDateTimeLocal(dateVal) {
      if (!dateVal) return ""
      const d = new Date(dateVal)
      if (isNaN(d.getTime())) return ""
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      const hours = String(d.getHours()).padStart(2, "0")
      const minutes = String(d.getMinutes()).padStart(2, "0")
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },

    onDateTimeInput(value) {
      if (!value || this.courseDialog.isEdit) return
      const selectedDate = new Date(value)
      if (isNaN(selectedDate.getTime())) return

      const oneHourBefore = new Date(selectedDate.getTime() - 60 * 60 * 1000)
      this.courseFormModel.cancellationDeadline = this.formatDateTimeLocal(oneHourBefore)
    },

    // Mobile calendar events dots
    getCalendarEvents(dateStr) {
      return this.courses.some((c) => {
        const courseDate = new Date(c.dateTime).toISOString().split("T")[0]
        return courseDate === dateStr
      })
    },

    // --- Course Formats Actions ---
    openCreateFormatDialog() {
      this.formatFormModel = {
        title: "",
        description: "",
        active: true,
      }
      this.formatDialog.isEdit = false
      this.formatDialog.editId = null
      this.formatDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.formatForm) this.$refs.formatForm.resetValidation()
      })
    },

    openEditFormatDialog(format) {
      this.formatFormModel = {
        title: format.title,
        description: format.description || "",
        active: format.active,
      }
      this.formatDialog.isEdit = true
      this.formatDialog.editId = format._id
      this.formatDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.formatForm) this.$refs.formatForm.resetValidation()
      })
    },

    async submitFormatForm() {
      this.formatDialog.saving = true
      try {
        if (this.formatDialog.isEdit) {
          await api.employeeCourses.admin.updateFormat(this.formatDialog.editId, this.formatFormModel)
          this.$toast.success("Kursformat aktualisiert.")
        } else {
          await api.employeeCourses.admin.createFormat(this.formatFormModel)
          this.$toast.success("Kursformat erfolgreich erstellt.")
        }
        this.formatDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Speichern fehlgeschlagen.")
      } finally {
        this.formatDialog.saving = false
      }
    },

    // --- Course Timeslots Actions ---
    openCreateDialog() {
      this.courseFormModel = {
        format: this.activeFormats.length > 0 ? this.activeFormats[0]._id : "",
        details: "",
        studio: this.studios.length > 0 ? this.studios[0]._id : "",
        dateTime: "",
        duration: 60,
        capacity: 20,
        waitlistEnabled: true,
        cancellationDeadline: "",
      }
      this.courseDialog.isEdit = false
      this.courseDialog.editId = null
      this.courseDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.courseForm) this.$refs.courseForm.resetValidation()
      })
    },

    openEditDialog(course) {
      this.courseFormModel = {
        format: course.format ? course.format._id : "",
        details: course.details || "",
        studio: course.studio ? course.studio._id : "",
        dateTime: this.formatDateTimeLocal(course.dateTime),
        duration: course.duration,
        capacity: course.capacity,
        waitlistEnabled: course.waitlistEnabled,
        cancellationDeadline: this.formatDateTimeLocal(course.cancellationDeadline),
      }
      this.courseDialog.isEdit = true
      this.courseDialog.editId = course._id
      this.courseDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.courseForm) this.$refs.courseForm.resetValidation()
      })
    },

    async submitCourseForm() {
      this.courseDialog.saving = true
      try {
        if (this.courseDialog.isEdit) {
          await api.employeeCourses.admin.updateCourse(this.courseDialog.editId, this.courseFormModel)
          this.$toast.success("Kurs erfolgreich aktualisiert.")
        } else {
          await api.employeeCourses.admin.createCourse(this.courseFormModel)
          this.$toast.success("Kurs erfolgreich geplant.")
        }
        this.courseDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Speichern des Kurses fehlgeschlagen.")
      } finally {
        this.courseDialog.saving = false
      }
    },

    confirmCancelCourse(course) {
      const title = course.format ? course.format.title : "Kurs"
      this.cancelCourseDialog = {
        show: true,
        courseId: course._id,
        courseTitle: title,
        loading: false,
      }
    },

    async executeCancelCourse() {
      this.cancelCourseDialog.loading = true
      try {
        await api.employeeCourses.admin.deleteCourse(this.cancelCourseDialog.courseId)
        this.$toast.success("Kurs erfolgreich abgesagt.")
        this.cancelCourseDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Absage fehlgeschlagen.")
      } finally {
        this.cancelCourseDialog.loading = false
      }
    },

    // --- Registrations Manager ---
    async openManageRegistrations(course) {
      this.regManager.course = course
      this.regManager.registrations = []
      this.regManager.selectedUserId = null
      this.regManager.selectedUser = null
      this.regManager.show = true
      await this.fetchRegistrations()
    },

    async fetchRegistrations() {
      if (!this.regManager.course) return
      this.regManager.loading = true
      try {
        const data = await api.employeeCourses.admin.fetchRegistrations(this.regManager.course._id)
        this.regManager.registrations = data || []
      } catch (e) {
        console.error(e)
        this.$toast.error("Teilnehmer konnten nicht geladen werden.")
      } finally {
        this.regManager.loading = false
      }
    },

    async onUserSelected(userId) {
      if (!userId) {
        this.regManager.selectedUserId = null
        this.regManager.selectedUser = null
        return
      }

      this.regManager.selectedUserId = userId
      try {
        const userDetails = await api.employeeCourses.admin.fetchUserDetails(userId)
        this.regManager.selectedUser = userDetails
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Mitarbeiterdetails.")
        this.regManager.selectedUserId = null
        this.regManager.selectedUser = null
      }
    },

    async submitManualRegistration() {
      if (!this.regManager.selectedUserId || !this.regManager.course) return
      this.regManager.addingUser = true
      try {
        await api.employeeCourses.admin.manualRegister(this.regManager.course._id, this.regManager.selectedUserId)
        this.$toast.success("Mitarbeiter erfolgreich eingebucht.")
        this.regManager.selectedUserId = null
        this.regManager.selectedUser = null
        await this.fetchRegistrations()
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Manuelle Buchung fehlgeschlagen.")
      } finally {
        this.regManager.addingUser = false
      }
    },

    async cancelUserRegistration(item) {
      if (!this.regManager.course) return
      this.regManager.loading = true
      try {
        await api.employeeCourses.admin.cancelRegistration(this.regManager.course._id, item._id)
        this.$toast.success("Buchung storniert.")
        await this.fetchRegistrations()
        await this.fetchData()
      } catch (e) {
        console.error(e)
        this.$toast.error("Stornierung fehlgeschlagen.")
      } finally {
        this.regManager.loading = false
      }
    },

    getRegStatusColor(item) {
      if (item.status === "registered") return "green accent-3"
      if (item.status === "waitlisted") return "orange"
      return "grey"
    },

    getRegStatusText(item) {
      if (item.status === "registered") return "Gebucht"
      if (item.status === "waitlisted") return `Warteliste (${item.waitlistPosition})`
      return "Storniert"
    },
  },
}
</script>

<style scoped>
.courses-admin-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -1px;
}

/* Cards */
.stat-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
}

/* Table Card */
.table-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.search-field :deep(.v-select__selection) {
  color: white !important;
}

.search-field :deep(.v-icon) {
  color: #fbc02d !important;
}

/* Custom Table Styles */
.custom-table :deep(table) {
  background-color: transparent !important;
}

.custom-table :deep(thead th) {
  font-weight: bold !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  color: #777777 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-table :deep(tbody td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.custom-table :deep(tbody tr:hover) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

.border-thin-outline {
  border: 1px solid rgba(255, 255, 255, 0.04) !important;
  border-radius: 8px !important;
}

.date-header-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #fbc02d;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(251, 192, 45, 0.6);
}

.meta-box {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.border-accent-box {
  border: 1px dashed rgba(251, 192, 45, 0.3) !important;
}

/* Mobile Styling */
.mobile-course-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  transition: all 0.2s ease;
}

.mobile-course-card.is-past {
  opacity: 0.7;
}

.mobile-course-card.is-cancelled {
  border-color: rgba(244, 67, 54, 0.3) !important;
  background-color: rgba(244, 67, 54, 0.02) !important;
}

.divider-thin {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.06);
}

.empty-state-mobile {
  background-color: #1c1c1e;
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.width-100 {
  width: 100%;
}

.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.gap-4 {
  gap: 16px;
}
</style>
